import React, { useState, useEffect } from 'react';
import { Play, Loader2, Eye, CandlestickChart, Activity, TrendingUp, LineChart, Building2, Filter, Bell, Video, ArrowRightLeft, Star } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface VideoLink {
  title: string;
  thumbnail: string;
  url: string;
}

interface VideoCategory {
  title: string;
  videos: VideoLink[];
}

const getCategoryIcon = (title: string) => {
  const normalizedTitle = title.toLowerCase().trim();
  const iconProps = { className: "h-7 w-7 md:h-8 md:w-8 mb-2 text-white drop-shadow-md", strokeWidth: 2.5 };
  
  switch (normalizedTitle) {
    case 'watchlist': return <Eye {...iconProps} />;
    case 'market analysis':
    case 'screener 360°': return <Filter {...iconProps} />;
    case 'broker analysis': return <Activity {...iconProps} />;
    case 'stock analysis': return <ArrowRightLeft {...iconProps} />;
    case 'market rotation': return <TrendingUp {...iconProps} />;
    case 'astrology': return <Star {...iconProps} />;
    case 'technical analysis': return <CandlestickChart {...iconProps} />;
    case 'bid-offer replay': return <Star {...iconProps} className="h-7 w-7 md:h-8 md:w-8 mb-2 text-yellow-400 fill-yellow-400 drop-shadow-md" />;
    case 'trend tracking': return <LineChart {...iconProps} />;
    case 'emiten info': return <Building2 {...iconProps} />;
    case 'technical screener': return <Filter {...iconProps} />;
    case 'real time alert': return <Bell {...iconProps} />;
    default: return <Video {...iconProps} />;
  }
};

export function VideoTutorial() {
  const [tutorialData, setTutorialData] = useState<VideoCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data, error } = await supabase
          .from('video_tutorials')
          .select('*')
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: true });

        if (error) {
          if (error.code !== '42P01') console.error('Error fetching tutorials:', error);
          setLoading(false);
          return;
        }

        const categoriesMap = new Map<string, VideoLink[]>();
        const categoryOrderMap = new Map<string, number>();
        
        (data || []).forEach((item: any) => {
          let thumbnail = item.custom_thumbnail_url;
          if (item.thumbnail_type === 'youtube' && item.youtube_url) {
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
            const match = item.youtube_url.match(regExp);
            const id = (match && match[2].length === 11) ? match[2] : null;
            if (id) thumbnail = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
          }

          if (!thumbnail) {
            // fallback
            thumbnail = 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=500&auto=format&fit=crop&q=60';
          }

          const videoLink: VideoLink = {
            title: item.title,
            thumbnail,
            url: item.youtube_url
          };

          if (!categoriesMap.has(item.category)) {
            categoriesMap.set(item.category, []);
          }
          categoriesMap.get(item.category)?.push(videoLink);
          
          if (!categoryOrderMap.has(item.category) || (item.category_order != null && item.category_order < categoryOrderMap.get(item.category)!)) {
             categoryOrderMap.set(item.category, item.category_order || 0);
          }
        });

        const formattedData: VideoCategory[] = Array.from(categoriesMap.entries()).map(([title, videos]) => ({
          title,
          videos
        }));
        
        formattedData.sort((a, b) => (categoryOrderMap.get(a.title) || 0) - (categoryOrderMap.get(b.title) || 0));

        setTutorialData(formattedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-1 md:px-2 h-full overflow-y-auto overflow-x-hidden">
      <div className="w-full space-y-2">
        <div>
          <p className="text-muted-foreground mt-1 text-base">Panduan lengkap menggunakan fitur-fitur BandarmoloNY</p>
        </div>

        <div className="bg-card border border-white/40 rounded-md overflow-hidden shadow-sm">
          <div className="flex flex-col">
            {tutorialData.map((category, index) => (
              <div 
                key={category.title} 
                className={`flex flex-col md:flex-row py-2 px-3 ${index !== tutorialData.length - 1 ? 'border-b border-white/40' : ''}`}
              >
                {/* Category Title - Left Column */}
                <div className="w-full md:w-36 lg:w-44 shrink-0 md:pr-3 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-white/40 py-2 md:py-0">
                  {getCategoryIcon(category.title)}
                  <h3 className="font-semibold text-sm text-card-foreground uppercase tracking-wider text-center">
                    {category.title.split(' ').map((word, i, arr) => (
                      <React.Fragment key={i}>
                        {word}
                        {i !== arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </h3>
                </div>

                {/* Thumbnails - Right Column */}
                <div className="flex-1 flex flex-wrap gap-2 md:pl-3 pt-1 md:pt-0">
                  {category.videos.map((video, vIndex) => (
                    <a
                      key={vIndex}
                      href={video.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex flex-col w-full sm:w-[278px] rounded-lg overflow-hidden border border-border bg-background transition-all hover:ring-2 hover:ring-primary/50 hover:shadow-md"
                    >
                      <div className="relative aspect-video overflow-hidden bg-muted">
                        <img 
                          src={video.thumbnail} 
                          alt={video.title} 
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="bg-primary/90 text-primary-foreground p-2 rounded-full backdrop-blur-sm">
                            <Play className="w-5 h-5 fill-current" />
                          </div>
                        </div>
                      </div>
                      <div className="p-2">
                        <h4 className="font-medium text-sm leading-tight text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                          {video.title}
                        </h4>
                      </div>
                    </a>
                  ))}
                  
                  {category.videos.length === 0 && (
                    <div className="text-muted-foreground text-sm py-4 italic">
                      Belum ada video tutorial.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {tutorialData.length === 0 && (
            <div className="text-muted-foreground text-center py-8">
              Belum ada video tutorial yang ditambahkan.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
